import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Skeleton, App } from 'antd'
import { ArrowLeft, Mic } from 'lucide-react'
import { OpenQuestion } from '../../survey/components/questions/OpenQuestion'
import { InterviewService } from '../services'
import { groupQuestionsBySource, sectionHeadingFor } from '../../validation'
import type { InterviewQuestion } from '@bandos/shared'
import { PageHeader, BackButton } from '../../../styles/pageHeaderStyles'
import { analytics } from '../../../utils/analytics'
import {
  CenterBox,
  ErrorMessage,
  Page,
  Content,
  HeaderLeft,
  HeaderMeta,
  BrandLabel,
  PageTitle,
  TimerBadge,
  NameInputWrapper,
  NameInput,
  SectionHeading,
  SectionLabel,
  SectionTitle,
  QuestionCard,
  QuestionMeta,
  QuestionIndex,
  AnsweredDot,
  Footer,
  ProgressText,
  FooterActions,
  FooterInner,
  ClearButton,
  SaveButton,
  SimulateButton,
  ProbeSection,
  ProbeLabel,
  ProbeList,
  ProbeItem,
} from './InterviewConductPageStyles'

export function InterviewConductPage() {
  const { nodeId } = useParams<{ nodeId: string }>()
  const navigate = useNavigate()
  const { message } = App.useApp()

  const [data, setData] = useState<{
    parentLabel: string
    interviewQuestions: InterviewQuestion[]
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [intervieweeName, setIntervieweeName] = useState('')
  const [saving, setSaving] = useState(false)
  const [simulating, setSimulating] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const startTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    if (!nodeId) {
      setError(true)
      setLoading(false)
      return
    }
    InterviewService.getInterviewData(nodeId)
      .then((res) => {
        if (res.error || !res.data) {
          setError(true)
        } else {
          setData(res.data)
          startTimeRef.current = Date.now()
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [nodeId])

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const handleChange = useCallback((index: number, value: string) => {
    setAnswers((prev) => ({ ...prev, [index]: value }))
  }, [])

  const handleClear = useCallback(() => setAnswers({}), [])

  const handleSimulate = useCallback(async () => {
    if (!nodeId || !data) return
    setSimulating(true)
    const res = await InterviewService.simulate(nodeId)
    setSimulating(false)
    if (res.error || !res.data) {
      void message.error('Could not generate simulated answers. Please try again.')
      return
    }
    const filled: Record<number, string> = {}
    res.data.answers.forEach((answer, i) => {
      filled[i] = answer
    })
    setAnswers(filled)
  }, [nodeId, data, message])

  const handleSave = useCallback(async () => {
    if (!nodeId || !data) return
    setSaving(true)
    const orderedAnswers: (string | null)[] = data.interviewQuestions.map(
      (_, i) => answers[i] ?? null
    )
    const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000)
    const res = await InterviewService.saveNotes(
      nodeId,
      orderedAnswers,
      durationSeconds,
      intervieweeName.trim() || undefined
    )
    setSaving(false)
    if (res.error) {
      void message.error('Failed to save notes. Please try again.')
      return
    }
    analytics.interviewConducted({
      node_id: nodeId,
      duration_seconds: durationSeconds,
      question_count: data.interviewQuestions.length,
    })
    void message.success('Notes saved!')
    void navigate(`/interview/${nodeId}/insights`, { replace: true })
  }, [nodeId, data, answers, intervieweeName, navigate, message])

  if (loading) {
    return (
      <Page>
        <Content>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Content>
      </Page>
    )
  }

  if (error || !data) {
    return (
      <CenterBox>
        <ErrorMessage>Interview not found.</ErrorMessage>
      </CenterBox>
    )
  }

  const { parentLabel, interviewQuestions } = data
  const total = interviewQuestions.length
  const answeredCount = Object.values(answers).filter((v) => isAnswered(v)).length

  return (
    <Page>
      <Content>
        <PageHeader>
          <HeaderLeft>
            <BackButton
              onClick={() => navigate(`/interview/${nodeId}/insights`, { replace: true })}
              title="Back"
            >
              <ArrowLeft size={18} />
            </BackButton>
            <HeaderMeta>
              <BrandLabel>
                <Mic size={13} />
                Record Interview
              </BrandLabel>
              <PageTitle>{parentLabel}</PageTitle>
            </HeaderMeta>
          </HeaderLeft>
          <TimerBadge>{formatElapsed(elapsed)}</TimerBadge>
        </PageHeader>

        <NameInputWrapper>
          <NameInput
            value={intervieweeName}
            onChange={(e) => setIntervieweeName(e.target.value)}
            placeholder="Interviewee name (optional)"
          />
        </NameInputWrapper>

        {groupQuestionsBySource(interviewQuestions).map((section) => (
          <div key={section.sourceNodeId ?? 'all'}>
            {section.sourceNodeId && (
              <SectionHeading>
                <SectionLabel>{sectionHeadingFor(section.sourceNodeCategory)}</SectionLabel>
                {section.sourceNodeLabel && <SectionTitle>{section.sourceNodeLabel}</SectionTitle>}
              </SectionHeading>
            )}
            {section.items.map(({ question: q, index: i }) => (
              <QuestionCard key={i} $answered={isAnswered(answers[i])}>
                <QuestionMeta>
                  <QuestionIndex>
                    Question {i + 1} of {total}
                  </QuestionIndex>
                  {isAnswered(answers[i]) && <AnsweredDot />}
                </QuestionMeta>
                <OpenQuestion
                  question={q.question}
                  value={answers[i] ?? ''}
                  onChange={(v) => handleChange(i, v)}
                />
                {q.probes && q.probes.length > 0 && (
                  <ProbeSection>
                    <ProbeLabel>If they give a short answer, ask:</ProbeLabel>
                    <ProbeList>
                      {q.probes.map((probe, pi) => (
                        <ProbeItem key={pi}>{probe}</ProbeItem>
                      ))}
                    </ProbeList>
                  </ProbeSection>
                )}
              </QuestionCard>
            ))}
          </div>
        ))}

        <Footer>
          <FooterInner>
            <ProgressText>
              {answeredCount} of {total} answered
            </ProgressText>
            <FooterActions>
              <ClearButton onClick={handleClear}>Clear all</ClearButton>
              <SimulateButton
                $loading={simulating}
                disabled={simulating || saving}
                onClick={handleSimulate}
              >
                {simulating ? 'Simulating…' : 'Simulate'}
              </SimulateButton>
              <SaveButton $saving={saving} disabled={saving || simulating} onClick={handleSave}>
                {saving ? 'Saving…' : 'Save notes'}
              </SaveButton>
            </FooterActions>
          </FooterInner>
        </Footer>
      </Content>
    </Page>
  )
}

function isAnswered(value: string | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
